import {
  focusHalo,
  focusHaloFlush,
  MorphPanel,
  panelMotion,
  press,
  usePanelDismiss,
} from "@pax/ui";
import { Check, ChevronDown, Search } from "lucide-react";
import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useEditable } from "./access";

/** The pieces every tab of the preset editor is built from. */

export const primaryButton = `h-9 rounded-full bg-foreground px-4 text-sm font-medium text-background hover:bg-foreground/85 disabled:opacity-40 ${panelMotion} ${press} ${focusHalo}`;

export const secondaryButton = `h-9 rounded-full bg-foreground/5 px-4 text-sm font-medium text-foreground hover:bg-foreground/10 disabled:opacity-40 ${panelMotion} ${press} ${focusHalo}`;

export const markButton = `grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground disabled:pointer-events-none disabled:opacity-40 ${panelMotion} ${press} ${focusHaloFlush}`;

export const sectionLabel = "text-xs text-muted-foreground";

export const listRow = `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left text-sm hover:bg-foreground/5 ${panelMotion} ${focusHaloFlush}`;

const dropdownSurface =
  "rounded-2xl bg-popover shadow-[0_4px_12px_rgb(0_0_0/0.06),0_24px_48px_-16px_rgb(0_0_0/0.2)] ring-1 ring-foreground/10";

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = "medium",
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  size?: "small" | "medium" | "tight";
}) {
  const sizes = {
    small: "h-7 px-2.5 text-xs",
    medium: "h-8 px-3 text-sm",
    tight: "h-8 px-2.5 text-[13px]",
  };
  return (
    <div className="inline-flex rounded-full bg-foreground/5 p-1">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={`${sizes[size]} rounded-full whitespace-nowrap ${panelMotion} ${focusHaloFlush} ${
            value === option.value
              ? "bg-background font-medium shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

type MenuAnchor = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/** How far a menu's face may scroll before the menu closes, so a nudge doesn't. */
const SCROLL_AWAY = 8;
/** How long after opening a menu the page may still be scrolling it into view. */
const SETTLING_MS = 400;
/** How much room below a menu's face it wants before it opens upward instead. */
const ROOM_BELOW = 320;
const MARGIN = 8;

/**
 * The trigger becomes the menu: pressed, the face grows into the panel it
 * opens, pinned to the corner it was pressed from. Everything that picks
 * from a list in the editor is one of these. The open menu floats over the
 * page, so a panel it's in never cuts it off, and opens upward when there
 * isn't room below.
 */
export function MorphMenu({
  face,
  label,
  anchor = "top-left",
  closedClassName = `rounded-2xl hover:bg-foreground/5 ${panelMotion}`,
  faceClassName = "h-8 px-3 text-sm",
  disabled = false,
  fit = false,
  defaultOpen = false,
  onClose,
  children,
}: {
  face: ReactNode;
  label: string;
  anchor?: MenuAnchor;
  closedClassName?: string;
  faceClassName?: string;
  disabled?: boolean;
  /**
   * The face is as wide as its words only while there's room: in less, its words give way with "…".
   * Its parent decides the room, so give it the space it may fill.
   */
  fit?: boolean;
  /** Open as it first appears. */
  defaultOpen?: boolean;
  /** However it closes. */
  onClose?: () => void;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const close = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = usePanelDismiss<HTMLDivElement>({ open, onDismiss: close, trigger: triggerRef });
  const faceLayout = `flex items-center gap-1.5 whitespace-nowrap ${faceClassName}`;

  return (
    <span
      ref={triggerRef}
      className={fit ? "relative block min-w-0 max-w-full" : "relative inline-block align-middle"}
    >
      <span className={open ? "invisible" : undefined}>
        <Closed fit={fit} anchor={anchor} className={closedClassName}>
          <button
            type="button"
            aria-label={label}
            aria-expanded={open}
            disabled={disabled}
            onClick={() => setOpen(true)}
            // A button is as wide as its words unless told otherwise, so a fitted one is told to fill.
            className={`${faceLayout} ${fit ? "w-full min-w-0" : ""} disabled:opacity-60 ${focusHaloFlush}`}
          >
            {face}
          </button>
        </Closed>
      </span>
      {open && (
        <FloatingMenu
          trigger={triggerRef}
          panelRef={panelRef}
          onScrolledAway={close}
          anchor={anchor}
          face={<span className={faceLayout}>{face}</span>}
          closedClassName={closedClassName}
        >
          {children(close)}
        </FloatingMenu>
      )}
    </span>
  );
}

/**
 * A closed menu's face. Fitted, it's laid out like anything else, as wide as its words up to the
 * room it has; otherwise it's a panel the size of its words, which never gives way.
 */
function Closed({
  fit,
  anchor,
  className,
  children,
}: {
  fit: boolean;
  anchor: MenuAnchor;
  className: string;
  children: ReactNode;
}) {
  if (!fit) {
    return (
      <MorphPanel view="closed" anchor={anchor} className={className}>
        {children}
      </MorphPanel>
    );
  }
  return <div className={`relative w-max max-w-full overflow-hidden ${className}`}>{children}</div>;
}

/** Where a floating menu stands: pinned by a corner to its face, as far as it may reach. */
interface Floating {
  anchor: MenuAnchor;
  style: CSSProperties;
  room: number;
}

/** An open menu over the page, growing out of its face where the face is, and following it. */
function FloatingMenu({
  trigger,
  panelRef,
  onScrolledAway,
  anchor,
  face,
  closedClassName,
  children,
}: {
  trigger: RefObject<HTMLSpanElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  /** Once the page scrolls its face out of place, as a menu left open would no longer belong. */
  onScrolledAway: () => void;
  anchor: MenuAnchor;
  face: ReactNode;
  closedClassName: string;
  children: ReactNode;
}) {
  const [placement, setPlacement] = useState<Floating | null>(null);
  const [grown, setGrown] = useState(false);
  const scrolledAway = useRef(onScrolledAway);
  scrolledAway.current = onScrolledAway;
  useLayoutEffect(() => {
    const place = () => {
      const box = trigger.current?.getBoundingClientRect();
      if (!box) return;
      const below = window.innerHeight - box.top - MARGIN;
      const above = box.bottom - MARGIN;
      const up = anchor.startsWith("bottom") || (below < ROOM_BELOW && above > below);
      const right = anchor.endsWith("right");
      /** How wide it can grow from the face before meeting the far side of the screen. */
      const across = right ? box.right - MARGIN : window.innerWidth - box.left - MARGIN;
      setPlacement({
        anchor: `${up ? "bottom" : "top"}-${right ? "right" : "left"}`,
        style: {
          ...({ "--menu-room": `${across}px` } as CSSProperties),
          position: "fixed",
          zIndex: 60,
          ...(up ? { bottom: window.innerHeight - box.bottom } : { top: box.top }),
          ...(right ? { right: window.innerWidth - box.right } : { left: box.left }),
        },
        room: up ? above : below,
      });
    };
    place();
    // Closes once its face has scrolled away, but not for scrolling within the menu, nor while
    // something in it is being typed into, as a phone's keyboard opening scrolls the page.
    let startedAt = trigger.current?.getBoundingClientRect();
    const openedAt = performance.now();
    const scrolled = (event: Event) => {
      const panel = panelRef.current;
      if (panel && event.target instanceof Node && panel.contains(event.target)) return;
      const typing = document.activeElement?.matches("input, textarea, select");
      if (typing && panel?.contains(document.activeElement)) return place();
      const box = trigger.current?.getBoundingClientRect();
      // Opening can itself scroll the page, bringing the face into view: where it settles counts.
      if (performance.now() - openedAt < SETTLING_MS) {
        startedAt = box;
        return place();
      }
      if (
        startedAt &&
        box &&
        Math.hypot(box.top - startedAt.top, box.left - startedAt.left) > SCROLL_AWAY
      ) {
        scrolledAway.current();
        return;
      }
      place();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", scrolled, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", scrolled, true);
    };
  }, [trigger, anchor, panelRef]);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  if (!placement) return null;
  return createPortal(
    <div ref={panelRef} style={placement.style}>
      <MorphPanel
        view={grown ? "open" : "closed"}
        anchor={placement.anchor}
        className={grown ? dropdownSurface : closedClassName}
      >
        {grown ? (
          <div className="overflow-y-auto overscroll-contain" style={{ maxHeight: placement.room }}>
            {children}
          </div>
        ) : (
          face
        )}
      </MorphPanel>
    </div>,
    document.body,
  );
}

export interface MorphOption<T extends string> {
  value: T;
  label: string;
  detail?: string;
  /** Stands for nothing chosen: chosen, it's greyed as a field's placeholder is. */
  none?: boolean;
}

export function MorphSelect<T extends string>({
  value,
  options,
  onChange,
  label,
  placeholder = "Choose",
  disabled,
  anchor,
  closedClassName,
  width = "w-64",
}: {
  value: T | null;
  options: MorphOption<T>[];
  onChange: (value: T) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  anchor?: MenuAnchor;
  closedClassName?: string;
  width?: string;
}) {
  const selected = options.find(option => option.value === value);
  // Choosing changes what it's of, so it's shut where your roles don't let you change that.
  const editable = useEditable();
  return (
    <MorphMenu
      label={label}
      anchor={anchor}
      disabled={disabled || !editable}
      closedClassName={closedClassName}
      face={
        <>
          <span
            className={
              !selected
                ? "text-muted-foreground"
                : selected.none
                  ? "font-medium text-muted-foreground/50"
                  : "font-medium"
            }
          >
            {selected?.label ?? placeholder}
          </span>
          {!disabled && <ChevronDown className="size-3.5 text-muted-foreground" />}
        </>
      }
    >
      {close => (
        <div className={`${width} p-1`}>
          {options.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                close();
              }}
              className={`flex w-full items-start justify-between gap-3 rounded-xl px-3 py-2 text-left text-sm hover:bg-foreground/[0.07] ${panelMotion} ${focusHaloFlush}`}
            >
              <span className="min-w-0">
                <span className={`block ${option.value === value ? "font-medium" : ""}`}>
                  {option.label}
                </span>
                {option.detail && (
                  <span className="block text-xs leading-5 text-muted-foreground">
                    {option.detail}
                  </span>
                )}
              </span>
              {option.value === value && <Check className="mt-1 size-3.5 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </MorphMenu>
  );
}

export function Switch({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: () => void;
  label: string;
  disabled?: boolean;
}) {
  const editable = useEditable();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled || !editable}
      onClick={onChange}
      className={`relative h-6 w-10 shrink-0 rounded-full disabled:opacity-50 ${panelMotion} ${focusHaloFlush} ${
        on ? "bg-foreground" : "bg-foreground/15"
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 size-5 rounded-full bg-background shadow-sm ${panelMotion} ${
          on ? "translate-x-4" : ""
        }`}
      />
    </button>
  );
}

export function Swatch({
  entity,
  size = "size-5",
}: {
  entity: { color: string; icon?: string } | undefined;
  size?: string;
}) {
  if (entity?.icon) {
    return (
      <img src={entity.icon} alt="" className={`${size} shrink-0 rounded-full object-cover`} />
    );
  }
  return (
    <span
      className={`${size} shrink-0 rounded-full ring-1 ring-foreground/10 ring-inset`}
      style={{ background: entity?.color ?? "transparent" }}
    />
  );
}

export function SearchField({
  value,
  onChange,
  placeholder,
  size = "medium",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  size?: "small" | "medium";
}) {
  const small = size === "small";
  return (
    <label
      className={`flex items-center rounded-full bg-foreground/5 focus-within:bg-foreground/[0.07] ${
        small ? "h-7 gap-1.5 px-2.5 text-xs" : "h-9 gap-2 px-3 text-sm"
      }`}
    >
      <Search className={`${small ? "size-3.5" : "size-4"} shrink-0 text-muted-foreground`} />
      <input
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
      />
    </label>
  );
}

/** How many people a list shows before the rest are asked for, in every section alike. */
export const PEOPLE_SHOWN = 10;

/** Opens the rest of a list of people, "Show all 15", and closes it again, "Show fewer". */
export function ShowAll({
  all,
  count,
  onToggle,
}: {
  all: boolean;
  count: number;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={all}
      onClick={onToggle}
      className={`flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground ${panelMotion}`}
    >
      <ChevronDown className={`size-3.5 transition-transform ${all ? "rotate-180" : ""}`} />
      {all ? "Show fewer" : `Show all ${count}`}
    </button>
  );
}

/** A box ticked or not, as every choice of several in the team is. */
export function Tick({ on }: { on: boolean }) {
  return (
    <span
      className={`grid size-3.5 shrink-0 place-items-center rounded ring-1 ${on ? "bg-foreground text-background ring-foreground" : "ring-foreground/25"}`}
    >
      {on && <Check className="size-2.5" strokeWidth={3} />}
    </span>
  );
}

/** One choice in a list of ticks: what it is, and whether it's yours to change. */
export function TickRow({
  on,
  allowed,
  onToggle,
  children,
}: {
  on: boolean;
  allowed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={!allowed}
      aria-pressed={on}
      onClick={onToggle}
      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm ${panelMotion} ${focusHaloFlush} ${allowed ? "hover:bg-foreground/[0.07]" : "cursor-default opacity-50"} ${on ? "text-foreground" : "text-muted-foreground"}`}
    >
      <Tick on={on} />
      {children}
    </button>
  );
}

const COLOURS = ["#e5484d", "#f76b15", "#d6a000", "#46a758", "#12a594", "#0090ff", "#8e4ec6"];

/** The colour a person is drawn in, the same everywhere they appear. */
export function colourOf(userId: string): string {
  let hash = 0;
  for (const character of userId) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return COLOURS[hash % COLOURS.length] as string;
}
